import { exportMappingsService } from '@/api/exportMappings';
import { memos } from '@/api/memos';
import type { Memo, MetamodelResponse } from '@/api/types';
import type { ExportOptionsStructured } from '@/api/types/export/mappings';
import { computed, ref } from 'vue';
import { useDataStructuring } from './useDataStructuring';
import { useTransformations } from './useTransformations';

// Types pour les mappings universels
export interface UniversalMapping {
  uuid: string;
  name: string;
  description?: string;
  format_type: 'json' | 'xml' | 'csv';
  is_active: boolean;
  field_mappings: UniversalFieldMapping[];
  export_options?: any[];
}

export interface UniversalFieldMapping {
  id?: number;
  source_entity: string;
  source_field: string;
  destination_path: string;
  transformation_rule?: string;
  static_value?: string;
  ordre: number;
  field_label?: string;
  field_description?: string;
}

export interface MappingResult {
  success: boolean;
  data: Record<string, any>;
  error?: string;
  format: string;
  content?: string;
}

export interface ImportMappingResult {
  success: boolean;
  memo_data: any;
  conflicts?: any[];
  alignment_requirements?: any;
  error?: string;
}

/**
 * Composable universel pour la gestion des mappings bidirectionnels
 * Permet d'importer et exporter des données selon des mappings configurables
 */
export function useMappings() {
  // ===============================
  // État réactif
  // ===============================
  
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const cachedMemos = ref<Map<string, Memo>>(new Map());
  const metamodel = ref<MetamodelResponse | null>(null);

  // ===============================
  // Composables externes
  // ===============================
  
  const transformations = useTransformations();
  const dataStructuring = useDataStructuring();

  // ===============================
  // Propriétés calculées
  // ===============================

  const hasError = computed(() => error.value !== null);
  const isLoadingComputed = computed(() => isLoading.value);

  // ===============================
  // Actions principales
  // ===============================

  /**
   * Charge le métamodèle universel
   */
  async function loadMetamodel(): Promise<MetamodelResponse | null> {
    if (metamodel.value) {
      return metamodel.value;
    }

    isLoading.value = true;
    error.value = null;

    try {
      const result = await exportMappingsService.getUniversalMetamodel();
      metamodel.value = result;
      return result;
    } catch (err) {
      error.value = `Erreur lors du chargement du métamodèle: ${err}`;
      console.error('Erreur loadMetamodel:', err);
      return null;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Applique un mapping pour exporter des données
   */
  async function applyExportMapping(
    mapping: UniversalMapping,
    memoUuid: string,
    format: 'json' | 'xml' | 'csv' = 'json'
  ): Promise<MappingResult> {
    isLoading.value = true;
    error.value = null;

    try {
      // Récupérer le mémo
      const memo = await getMemo(memoUuid);
      if (!memo) {
        return {
          success: false,
          data: {},
          error: 'Mémo non trouvé',
          format
        };
      }

      // Appliquer le mapping
      const extractedData = await extractDataWithMapping(memo, mapping);
      
      // Structurer les données
      const structuredData = dataStructuring.structureDataForExport(extractedData);
      
      // Appliquer les options d'export
      const exportOptions = parseExportOptions(mapping.export_options || []);
      const processedData = applyExportOptions(structuredData, exportOptions);

      // Formater selon le type demandé
      let content: string;
      switch (format) {
        case 'xml':
          content = convertToXML(processedData);
          break;
        case 'csv':
          content = convertToCSV(processedData);
          break;
        case 'json':
        default:
          // Pour les QR codes, on compactera le JSON (pas de retours à la ligne / espaces inutiles)
          // tout en conservant les clés complètes.
          content = JSON.stringify(processedData, null, exportOptions.qr_code_optimized ? 0 : 2);
          break;
      }

      return {
        success: true,
        data: processedData,
        format,
        content
      };

    } catch (err) {
      const errorMsg = `Erreur lors de l'application du mapping: ${err}`;
      error.value = errorMsg;
      console.error('Erreur applyExportMapping:', err);
      return {
        success: false,
        data: {},
        error: errorMsg,
        format
      };
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Applique un mapping pour importer des données JSON
   */
  async function applyImportMapping(
    mapping: UniversalMapping,
    jsonData: Record<string, any>
  ): Promise<ImportMappingResult> {
    isLoading.value = true;
    error.value = null;

    try {
      // Valider les données d'entrée
      if (!jsonData || typeof jsonData !== 'object') {
        return {
          success: false,
          memo_data: {},
          error: 'Données JSON invalides'
        };
      }

      // Appliquer le mapping inverse
      const memoData = await transformJsonToMemoData(jsonData);
      
      // Détecter les conflits
      const conflicts = await detectConflicts();
      
      // Identifier les besoins d'alignement
      const alignmentRequirements = await identifyAlignmentRequirements();

      return {
        success: true,
        memo_data: memoData,
        conflicts,
        alignment_requirements: alignmentRequirements
      };

    } catch (err) {
      const errorMsg = `Erreur lors de l'application du mapping d'import: ${err}`;
      error.value = errorMsg;
      console.error('Erreur applyImportMapping:', err);
      return {
        success: false,
        memo_data: {},
        error: errorMsg
      };
    } finally {
      isLoading.value = false;
    }
  }

  // ===============================
  // Fonctions utilitaires
  // ===============================

  /**
   * Récupère un mémo depuis le cache ou l'API
   */
  async function getMemo(uuid: string): Promise<Memo | null> {
    if (cachedMemos.value.has(uuid)) {
      return cachedMemos.value.get(uuid)!;
    }

    try {
      isLoading.value = true;
      error.value = null;
      
      const fetchedMemo = await memos.getByUuid(uuid);
      cachedMemos.value.set(uuid, fetchedMemo);
      return fetchedMemo;
    } catch (err) {
      error.value = `Erreur lors du chargement du mémo: ${err}`;
      console.error('Erreur getMemo:', err);
      return null;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Extrait les données selon le mapping
   */
  async function extractDataWithMapping(
    memo: Memo, 
    mapping: UniversalMapping
  ): Promise<Record<string, any>> {
    const result: Record<string, any> = {};
    
    // Trier les champs par ordre
    const sortedFields = [...mapping.field_mappings].sort((a, b) => a.ordre - b.ordre);
    
    for (const fieldMapping of sortedFields) {
      const value = extractFieldValue(memo, fieldMapping);
      
      // Ne pas ajouter les valeurs null/undefined ou les objets vides
      if (value !== null && value !== undefined) {
        // Pour les objets, vérifier qu'ils ne sont pas vides
        if (typeof value === 'object' && !Array.isArray(value)) {
          if (Object.keys(value).length > 0) {
            result[fieldMapping.destination_path] = value;
          }
        } else {
          result[fieldMapping.destination_path] = value;
        }
      }
    }
    
    return result;
  }

  /**
   * Extrait une valeur selon le mapping de champ
   */
  function extractFieldValue(memo: Memo, fieldMapping: UniversalFieldMapping): any {
    const { source_entity, source_field, static_value, transformation_rule } = fieldMapping;
    
    // Valeur statique
    if (source_entity === 'static') {
      return static_value || '';
    }

    // Vérifier si c'est un mapping automatique avec auto_hierarchy
    if (transformation_rule && transformation_rule.includes('auto_hierarchy:')) {
      const autoHierarchyMatch = transformation_rule.match(/auto_hierarchy:([^|]+)/);
      if (autoHierarchyMatch) {
        const prefix = autoHierarchyMatch[1];
        const otherRules = transformation_rule.replace(/auto_hierarchy:[^|]+/, '').replace(/^\||\|$/, '');
        
        // Appliquer le mapping automatique
        const autoMappingResult = transformations.applyAutomaticSectionMapping(memo, prefix, fieldMapping.destination_path || '', otherRules);
        
        // Retourner l'objet complet pour les mappings automatiques
        return autoMappingResult;
      }
    }

    // Extraire la valeur brute de manière universelle
    const rawValue = transformations.extractUniversalFieldValue(memo, source_entity, source_field);

    // Appliquer les règles de transformation si elles existent
    if (transformation_rule && transformation_rule.trim()) {
      const transformedValue = transformations.applyTransformationRules(rawValue, transformation_rule, memo);
      return transformedValue;
    }

    return rawValue;
  }

  /**
   * Transforme des données JSON en structure Memo selon le mapping
   */
  async function transformJsonToMemoData(
    jsonData: Record<string, any>
  ): Promise<any> {
    // Logique de transformation inverse
    // À implémenter selon les besoins spécifiques
    return jsonData;
  }

  /**
   * Détecte les conflits lors de l'import
   */
  async function detectConflicts(): Promise<any[]> {
    // Logique de détection de conflits
    // À implémenter selon les besoins spécifiques
    return [];
  }

  /**
   * Identifie les besoins d'alignement lors de l'import
   */
  async function identifyAlignmentRequirements(): Promise<any> {
    // Logique d'identification des besoins d'alignement
    // À implémenter selon les besoins spécifiques
    return {};
  }

  // ===============================
  // Fonctions de formatage
  // ===============================

  /**
   * Convertit les données en XML
   */
  function convertToXML(data: Record<string, any>): string {
    function objectToXML(obj: any, rootName = 'root'): string {
      if (obj === null || obj === undefined) {
        return `<${rootName}></${rootName}>`;
      }
      
      if (typeof obj !== 'object') {
        return `<${rootName}>${String(obj)}</${rootName}>`;
      }
      
      if (Array.isArray(obj)) {
        return obj.map(item => objectToXML(item, rootName)).join('\n');
      }
      
      let xml = `<${rootName}>`;
      for (const [key, value] of Object.entries(obj)) {
        xml += '\n  ' + objectToXML(value, key).replace(/\n/g, '\n  ');
      }
      xml += `\n</${rootName}>`;
      return xml;
    }
    
    return '<?xml version="1.0" encoding="UTF-8"?>\n' + objectToXML(data, 'MEMoS');
  }

  /**
   * Convertit les données en CSV
   */
  function convertToCSV(data: Record<string, any>): string {
    function flattenObject(obj: any, prefix = ''): Record<string, any> {
      const flattened: Record<string, any> = {};
      
      for (const [key, value] of Object.entries(obj)) {
        const newKey = prefix ? `${prefix}.${key}` : key;
        
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          Object.assign(flattened, flattenObject(value, newKey));
        } else if (Array.isArray(value)) {
          flattened[newKey] = JSON.stringify(value);
        } else {
          flattened[newKey] = value;
        }
      }
      
      return flattened;
    }
    
    const flattened = flattenObject(data);
    const headers = Object.keys(flattened);
    const values = Object.values(flattened);
    
    return headers.join(',') + '\n' + values.map(v => `"${String(v || '')}"`).join(',');
  }

  // ===============================
  // Fonctions d'options d'export
  // ===============================

  /**
   * Convertit les ExportOptions en structure utilisable
   */
  function parseExportOptions(exportOptions: any[]): ExportOptionsStructured {
    const defaultOptions: ExportOptionsStructured = {
      skip_empty_fields: false,
      qr_code_optimized: false,
      include_metadata: true,
      custom_date_format: undefined
    };

    if (!exportOptions || !Array.isArray(exportOptions)) {
      return defaultOptions;
    }

    const parsed = { ...defaultOptions };
    
    for (const option of exportOptions) {
      switch (option.option_key) {
        case 'skip_empty_fields':
          parsed.skip_empty_fields = option.option_value === 'true';
          break;
        case 'qr_code_optimized':
          parsed.qr_code_optimized = option.option_value === 'true';
          break;
        case 'include_metadata':
          parsed.include_metadata = option.option_value === 'true';
          break;
        case 'custom_date_format':
          parsed.custom_date_format = option.option_value || undefined;
          break;
      }
    }

    return parsed;
  }

  /**
   * Applique les options d'export sur les données
   */
  function applyExportOptions(
    data: Record<string, any>, 
    options: ExportOptionsStructured
  ): Record<string, any> {
    let result = { ...data };

    // Supprimer les champs vides si demandé
    if (options.skip_empty_fields) {
      result = dataStructuring.removeEmptyFields(result);
    }

    // Optimisation QR Code
    if (options.qr_code_optimized) {
      result = optimizeForQRCode(result);
    }

    // Formater les dates si format personnalisé
    if (options.custom_date_format) {
      result = formatDates(result, options.custom_date_format);
    }

    // Ajouter/supprimer les métadonnées
    if (!options.include_metadata) {
      result = removeMetadataFields(result);
    }

    return result;
  }

  /**
   * Optimise les données pour QR Code.
   *
   * IMPORTANT (15/12/2025): on ne raccourcit plus les clés pour les QR codes.
   * Le "gain de place" se fait via un JSON compacté au moment du stringify.
   */
  function optimizeForQRCode(data: Record<string, any>): Record<string, any> {
    return data;
  }

  /**
   * Formate les dates selon le format spécifié
   */
  function formatDates(obj: any, dateFormat: string): any {
    if (obj === null || obj === undefined) {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(item => formatDates(item, dateFormat));
    }

    if (typeof obj === 'object') {
      const result: Record<string, any> = {};
      for (const [key, value] of Object.entries(obj)) {
        if (typeof value === 'string' && isDateString(value)) {
          result[key] = formatDateString(value, dateFormat);
        } else {
          result[key] = formatDates(value, dateFormat);
        }
      }
      return result;
    }

    return obj;
  }

  /**
   * Vérifie si une chaîne est une date ISO
   */
  function isDateString(str: string): boolean {
    return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(str);
  }

  /**
   * Formate une date selon le format spécifié
   */
  function formatDateString(dateStr: string, format: string): string {
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;

      switch (format) {
        case 'YYYY-MM-DD':
          return date.toISOString().split('T')[0];
        case 'DD/MM/YYYY':
          return date.toLocaleDateString('fr-FR');
        case 'MM/DD/YYYY':
          return date.toLocaleDateString('en-US');
        case 'timestamp':
          return date.getTime().toString();
        default:
          return dateStr;
      }
    } catch {
      return dateStr;
    }
  }

  /**
   * Supprime les champs de métadonnées
   */
  function removeMetadataFields(obj: any): any {
    if (obj === null || obj === undefined || typeof obj !== 'object') {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(removeMetadataFields);
    }

    const metadataFields = ['created_at', 'updated_at', 'user_id', 'is_deleted', 'is_public'];
    const result: Record<string, any> = {};
    
    for (const [key, value] of Object.entries(obj)) {
      if (!metadataFields.includes(key)) {
        result[key] = removeMetadataFields(value);
      }
    }
    
    return result;
  }

  // ===============================
  // Fonctions utilitaires
  // ===============================

  /**
   * Télécharge le fichier généré
   */
  function downloadFile(content: string, filename: string, mimeType: string): void {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Vide le cache des mémos
   */
  function clearCache(): void {
    cachedMemos.value.clear();
  }

  /**
   * Remet à zéro l'état du composable
   */
  function reset(): void {
    isLoading.value = false;
    error.value = null;
    metamodel.value = null;
    clearCache();
  }

  // ===============================
  // Interface publique
  // ===============================

  return {
    // État
    isLoading: isLoadingComputed,
    error: computed(() => error.value),
    hasError,
    isLoadingRaw: isLoading,
    errorRaw: error,
    
    // Actions principales
    loadMetamodel,
    applyExportMapping,
    applyImportMapping,
    getMemo,
    
    // Utilitaires
    downloadFile,
    clearCache,
    reset,
    
    // Fonctions de formatage
    convertToXML,
    convertToCSV,
    
    // Fonctions d'options
    parseExportOptions,
    applyExportOptions
  };
} 